import React from 'react';
import { Inbox } from './CustomIcons';

/**
 * EmptyState — reusable empty/zero-data placeholder.
 *
 * Props:
 *   icon       LucideIcon component (default: Inbox)
 *   title      Primary message
 *   subtitle   Secondary description
 *   action     { label: string, onClick: fn } — optional CTA button
 *   compact    bool — smaller layout for inline use (default: false)
 *   testId     data-testid value
 */
export function EmptyState({
  icon: Icon = Inbox,
  title = 'Nothing here yet',
  subtitle = '',
  action = null,
  compact = false,
  testId = 'empty-state',
}) {
  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        textAlign: 'center',
        padding: compact ? '24px 16px' : '48px 24px',
        gap: compact ? '10px' : '16px',
      }}
    >
      <div style={{
        width: compact ? '44px' : '60px',
        height: compact ? '44px' : '60px',
        borderRadius: compact ? '12px' : '16px',
        background: 'rgba(249,115,22,0.08)',
        border: '1px solid rgba(249,115,22,0.15)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        <Icon
          size={compact ? 20 : 28}
          color="#F97316"
          aria-hidden="true"
          strokeWidth={1.5}
        />
      </div>

      <div>
        <p style={{
          fontFamily: 'Plus Jakarta Sans, sans-serif',
          fontWeight: 700,
          fontSize: compact ? '14px' : '16px',
          color: 'var(--text-primary)',
          margin: '0 0 4px 0',
        }}>
          {title}
        </p>
        {subtitle && (
          <p style={{
            fontSize: compact ? '12px' : '13px',
            color: 'var(--text-muted)',
            fontFamily: 'Inter, sans-serif',
            margin: 0,
            lineHeight: 1.5,
            maxWidth: '260px',
          }}>
            {subtitle}
          </p>
        )}
      </div>

      {action && (
        <button
          onClick={action.onClick}
          style={{
            background: 'rgba(249,115,22,0.1)',
            border: '1px solid rgba(249,115,22,0.25)',
            color: '#F97316',
            borderRadius: '8px',
            padding: '8px 20px',
            fontSize: '13px',
            fontFamily: 'Plus Jakarta Sans, sans-serif',
            fontWeight: 600,
            cursor: 'pointer',
            transition: 'background 0.15s',
            minHeight: '44px',
          }}
          onMouseEnter={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.18)'; }}
          onMouseLeave={e => { e.currentTarget.style.background = 'rgba(249,115,22,0.1)'; }}
          data-testid={`${testId}-action`}
        >
          {action.label}
        </button>
      )}
    </div>
  );
}

export default EmptyState;
